/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/
'use client';

import React from 'react';
import {usePathname} from 'next/navigation';
import {FaFacebook, FaInstagram, FaTiktok} from "react-icons/fa6";
import AppStatic from "@/utils/AppStatic";
import Link from "next/link";
import Image from "next/image";
import WeddifyLogo from "@/assets/images/weddify-logo.webp";
import netopiaLogo from "@/assets/images/footer/netopia.svg";
import salLogo from "@/assets/images/footer/sal-big.webp";
import solLogo from "@/assets/images/footer/sol-big.webp";
import footerImage from "@/assets/images/slides/slide-4.webp";

const AppFooter = () => {
    const pathname = usePathname();

    if (pathname.includes('/setup') || pathname.includes('/order')) {
        return;
    }

    return (
        <footer className="relative">
			<div
				className="absolute top-0 left-0 w-full min-h-full flex items-center bg-center bg-cover bg-no-repeat">
				<Image src={footerImage} alt={'Weddify by Diaphan'}
					   className="w-full h-full object-cover"
					   fill={true}
				/>
			</div>
            <div
                className="w-full min-h-full flex items-center bg-center bg-cover bg-no-repeat">
                <div className="w-full overflow-hidden bg-white/60 z-1">
                    <div
                        className="w-full mt-3 items-center gap-4 grid grid-cols-1 lg:grid-cols-4 pt-4 md:pt-10 pl-8 pr-8 mb-6">
                        <div>
                            <Link href={'/'}>
                                <Image src={WeddifyLogo}
                                       className="w-full hover:scale-105 transition-transform ease-in-out duration-300"
                                       alt="Weddify by Diaphan" loading="lazy"/>
                            </Link>
                        </div>
                        <div>
                            <p className="mt-3.5 text-xl uppercase font-bold mb-3">
                                {AppStatic.appFooterInfoHeading.ro.translation}
                            </p>
                            <ul className="block">
                                <li className="mb-2 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <Link target="_blank" href="https://diaphan.ro/despre-noi/">{
                                        AppStatic.footerAboutUs.ro.translation}
                                    </Link>
                                </li>
                                <li className="mb-2 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <Link href="/info/termeni-si-conditii">
                                        {AppStatic.footerTermsHeader.ro.translation}
                                    </Link>
                                </li>
                                <li className="mb-2 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <Link href="/info/politica-confidentialitate">
                                        {AppStatic.footerConfidentialityHeader.ro.translation}
                                    </Link>
                                </li>
                                <li className="mb-2 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <Link href="/info/politica-cookies">
                                        {AppStatic.footerCookiesHeader.ro.translation}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <p className="mt-3.5 text-lg uppercase font-bold mb-3">
                                {AppStatic.appFooterShopHeading.ro.translation}
                            </p>
                            <ul className="block">
                                <li className="mb-2 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <Link target="_blank" href="https://diaphan.ro/papetarie/">
                                        {AppStatic.appFooterShopStationeryHeading.ro.translation}
                                    </Link>
                                </li>
                                <li className="mb-2 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <Link target="_blank" href="https://diaphan.ro/floristica/">
                                        {AppStatic.appFooterShopFloralHeading.ro.translation}
                                    </Link>
                                </li>
                                <li className="mb-2 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <Link target="_blank" href="https://diaphan.ro/invitatii-nunta/">
                                        {AppStatic.appFooterShopWeddingInvitesHeading.ro.translation}
                                    </Link>
                                </li>
                                <li className="mb-2 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <Link target="_blank" href="https://diaphan.ro/shop/">
                                        {AppStatic.appFooterShopMiscHeading.ro.translation}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div className="mt-3 lg:mt-0">
                            <Link target="_blank" href="https://netopia-payments.com/">
                                <Image
                                    className="w-80 lg:w-full mb-2 bg-black rounded-xl hover:scale-105 transition-transform ease-in-out duration-300"
                                    src={netopiaLogo} alt="Weddify By Diaphan"
                                    loading="lazy"/>
                            </Link>
                            <div className="flex gap-2">
                                <Link target="_blank" href="https://anpc.ro/ce-este-sal/">
                                    <Image
                                        className="w-60 lg:w-full hover:scale-105 transition-transform ease-in-out duration-300"
                                        src={salLogo} alt="Weddify By Diaphan"
                                        loading="lazy"/>
                                </Link>
                                <Link target="_blank" href="https://ec.europa.eu/consumers/odr">
                                    <Image
                                        className="w-60 lg:w-full  hover:scale-105 transition-transform ease-in-out duration-300"
                                        src={solLogo} alt="Weddify By Diaphan"
                                        loading="lazy"/>
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div className="w-full mt-0 text-center pb-10 pt-10">
                        <div className="flex items-center gap-3 justify-center">
                            <a href="https://www.facebook.com/diaphan.stationery" rel="noreferrer"
                               target="_blank"
                               className="hover:scale-105 transition-transform ease-in-out duration-300">
                                <FaFacebook className="text-3xl"/>
                            </a>
                            <a href="https://www.instagram.com/diaphan.stationery/" rel="noreferrer"
                               target="_blank"
                               className="hover:scale-105 transition-transform ease-in-out duration-300">
                                <FaInstagram className="text-3xl"/>
                            </a>
                            <a href="https://www.tiktok.com/@diaphan.ro" rel="noreferrer" target="_blank"
                               className="hover:scale-105 transition-transform ease-in-out duration-300">
                                <FaTiktok className="text-3xl"/>
                            </a>
                        </div>
                        <div className="text-center mb-2 font-bold text-mono-1000">
                            <p className="font-normal mt-2">{AppStatic.appCopyright.ro.translation}</p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default AppFooter;
